import { articles } from '@/mocks/articles';
import { specials } from '@/mocks/specials';
import { categories, categoryMap } from '@/mocks/categories';
import type { Article, Special, CategoryInfo } from '@/types';

export type SortKey = 'latest' | 'hot' | 'views';
export type RangeKey = 'all' | 'today' | '3d' | '7d';

export function getAllArticles(): Article[] {
  return [...articles];
}

export function getArticleById(id: string): Article | undefined {
  return articles.find((item) => item.id === id);
}

export function getCategories(): CategoryInfo[] {
  return categories;
}

export function getCategoryBySlug(slug?: string): CategoryInfo | undefined {
  if (!slug) return undefined;
  return categories.find((item) => item.slug === slug);
}

export function getCategoryName(slug?: string): string {
  if (!slug) return '';
  return categoryMap[slug as keyof typeof categoryMap] || '';
}

export function getAllSpecials(): Special[] {
  return [...specials];
}

export function getSpecialById(id: string): Special | undefined {
  return specials.find((item) => item.id === id);
}

export function getArticlesByIds(ids: string[]): Article[] {
  return ids.map((id) => articles.find((item) => item.id === id)).filter((a): a is Article => Boolean(a));
}

function withinRange(iso: string, range: RangeKey): boolean {
  if (range === 'all') return true;
  const diff = Date.now() - new Date(iso).getTime();
  const day = 86400000;
  if (range === 'today') return diff <= day;
  if (range === '3d') return diff <= 3 * day;
  if (range === '7d') return diff <= 7 * day;
  return true;
}

function sortArticles(list: Article[], sort: SortKey): Article[] {
  const arr = [...list];
  if (sort === 'views') return arr.sort((a, b) => b.views - a.views);
  if (sort === 'hot')
    return arr.sort((a, b) => Number(b.hot) - Number(a.hot) || b.views - a.views);
  return arr.sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
}

export interface FilterOptions {
  category?: string;
  sub?: string;
  range?: RangeKey;
  sort?: SortKey;
  keyword?: string;
}

export function filterArticles(options: FilterOptions): Article[] {
  const { category, sub = 'all', range = 'all', sort = 'latest', keyword = '' } = options;
  let list = [...articles];
  if (category) list = list.filter((item) => item.category === category);
  if (sub && sub !== 'all') list = list.filter((item) => item.subCategory === sub);
  if (range !== 'all') list = list.filter((item) => withinRange(item.publishTime, range));
  const kw = keyword.trim().toLowerCase();
  if (kw) {
    list = list.filter(
      (item) =>
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((t) => t.toLowerCase().includes(kw)),
    );
  }
  return sortArticles(list, sort);
}

export function getRelatedArticles(article: Article, limit = 4): Article[] {
  const sameCategory = articles.filter((item) => item.category === article.category && item.id !== article.id);
  const others = articles.filter((item) => item.category !== article.category && item.id !== article.id);
  return [...sameCategory, ...others].slice(0, limit);
}

export function getFeaturedArticles(limit = 5): Article[] {
  const featured = articles.filter((item) => item.featured);
  if (featured.length >= limit) return featured.slice(0, limit);
  const rest = sortArticles(articles, 'hot').filter((a) => !a.featured);
  return [...featured, ...rest].slice(0, limit);
}